import type { InvoiceStatus } from '@stayafrica/contracts';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChipFilter,
  Container,
  EmptyState,
  ErrorState,
  Input,
  KpiCard,
  NoPermissionState,
  PageHeader,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
  UserCell,
} from '@stayafrica/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  KeyRound,
  List,
  Lock,
  Receipt,
  RotateCcw,
  Search,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/features/auth/session';
import { invoicesQueryOptions } from '@/features/billing/queries';
import { INVOICE_STATUSES, invoiceTone, overdueDays } from '@/features/billing/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateShort } from '@/lib/format';

interface InvoicesSearch {
  q?: string;
  status?: string;
  overdue?: boolean;
}

export const Route = createFileRoute('/_app/billing/invoices/')({
  validateSearch: (search: Record<string, unknown>): InvoicesSearch => ({
    ...(typeof search['q'] === 'string' ? { q: search['q'] } : {}),
    ...(typeof search['status'] === 'string' ? { status: search['status'] } : {}),
    ...(search['overdue'] === true || search['overdue'] === 'true' ? { overdue: true } : {}),
  }),
  component: InvoicesPage,
});

function InvoicesPage() {
  const { t, i18n } = useTranslation(['billing', 'common', 'errors']);
  const search = Route.useSearch();
  const canRead = useCan('invoice.read');

  const [term, setTerm] = useState(search.q ?? '');
  const [debounced, setDebounced] = useState(search.q ?? '');
  const [statuses, setStatuses] = useState<InvoiceStatus[]>(
    search.status ? (search.status.split(',') as InvoiceStatus[]) : [],
  );
  const [balanceDue, setBalanceDue] = useState(false);
  const [overdue, setOverdue] = useState(search.overdue === true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.length >= 2 ? term : ''), 250);
    return () => window.clearTimeout(timer);
  }, [term]);

  const filters = {
    ...(debounced ? { q: debounced } : {}),
    ...(statuses.length > 0 ? { status: statuses.join(',') } : {}),
    ...(balanceDue ? { balanceDue: true } : {}),
    ...(overdue ? { overdue: true } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };

  const query = useInfiniteQuery(invoicesQueryOptions(filters, 25, canRead));
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const issued = rows.filter((row) => row.status !== 'DRAFT' && row.status !== 'CANCELLED');
  const invoicedTotal = issued.reduce((sum, row) => sum + row.totals.netTotal, 0);
  const collectedTotal = issued.reduce((sum, row) => sum + row.totals.paidTotal, 0);
  const dueTotal = issued.reduce((sum, row) => sum + Math.max(row.totals.balance, 0), 0);
  const overdueTotal = issued
    .filter((row) => overdueDays(row) > 0)
    .reduce((sum, row) => sum + Math.max(row.totals.balance, 0), 0);
  const blockedDrafts = rows.filter((row) => row.status === 'DRAFT').length;
  const collectedRate = invoicedTotal > 0 ? Math.round((collectedTotal / invoicedTotal) * 100) : 0;

  const toggleStatus = (status: InvoiceStatus) =>
    setStatuses((current) =>
      current.includes(status) ? current.filter((value) => value !== status) : [...current, status],
    );

  const hasFilters =
    debounced !== '' || statuses.length > 0 || balanceDue || overdue || from !== '' || to !== '';

  return (
    <Container>
      <PageHeader
        title={t('billing:invoices.title')}
        subtitle={t('billing:invoices.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('billing:invoices.title') },
        ]}
        actions={
          <Button variant="secondary" asChild>
            <Link to="/billing/credit-notes">
              <RotateCcw className="ico" aria-hidden="true" />
              {t('billing:creditNotes.title')}
            </Link>
          </Button>
        }
      />

      <div className="stack">
        <div className="grid grid-4">
          <KpiCard
            tone="violet"
            icon={<Receipt className="ico ico-16" aria-hidden="true" />}
            label={t('billing:invoices.kpi.invoiced')}
            value={formatAmount(invoicedTotal, i18n.language)}
            loading={query.isPending}
            footer={t('billing:invoices.kpi.invoicedFoot', { count: issued.length })}
          />
          <KpiCard
            tone="green"
            icon={<CheckCircle2 className="ico ico-16" aria-hidden="true" />}
            label={t('billing:invoices.kpi.collected')}
            value={formatAmount(collectedTotal, i18n.language)}
            loading={query.isPending}
            footer={t('billing:invoices.kpi.collectedFoot', { rate: collectedRate })}
          />
          <KpiCard
            tone="amber"
            icon={<Clock className="ico ico-16" aria-hidden="true" />}
            label={t('billing:invoices.kpi.due')}
            value={formatAmount(dueTotal, i18n.language)}
            loading={query.isPending}
            footer={t('billing:invoices.kpi.dueFoot', {
              amount: formatAmount(overdueTotal, i18n.language),
            })}
          />
          <KpiCard
            tone="red"
            icon={<Lock className="ico ico-16" aria-hidden="true" />}
            label={t('billing:invoices.kpi.blocked')}
            value={blockedDrafts}
            loading={query.isPending}
            footer={t('billing:invoices.kpi.blockedFoot')}
          />
        </div>

        <Card>
          <CardHeader
            title={t('billing:invoices.listTitle')}
            icon={<List className="ico ico-16" aria-hidden="true" />}
            iconTone="blue"
            actions={
              <Badge tone="neutral">
                {t('billing:invoices.countLabel', { count: rows.length })}
              </Badge>
            }
          />

          <Toolbar>
            <Input
              type="search"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={t('billing:invoices.searchPlaceholder')}
              aria-label={t('billing:invoices.searchLabel')}
              icon={<Search className="ico" aria-hidden="true" />}
              style={{ width: 280, height: 32, fontSize: 'var(--fs-13)' }}
            />
            {INVOICE_STATUSES.map((status) => (
              <ChipFilter
                key={status}
                active={statuses.includes(status)}
                onClick={() => toggleStatus(status)}
              >
                {t(`billing:status.invoice.${status}`)}
              </ChipFilter>
            ))}
          </Toolbar>

          <Toolbar>
            <ChipFilter active={balanceDue} onClick={() => setBalanceDue((value) => !value)}>
              {t('billing:invoices.filters.balanceDue')}
            </ChipFilter>
            <ChipFilter active={overdue} onClick={() => setOverdue((value) => !value)}>
              {t('billing:invoices.filters.overdue')}
            </ChipFilter>
            <Input
              type="date"
              aria-label={t('billing:invoices.filters.from')}
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              style={{ width: 170, height: 30, fontSize: 'var(--fs-13)' }}
            />
            <Input
              type="date"
              aria-label={t('billing:invoices.filters.to')}
              value={to}
              onChange={(event) => setTo(event.target.value)}
              style={{ width: 170, height: 30, fontSize: 'var(--fs-13)' }}
            />
            <span className="spacer" />
            {hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTerm('');
                  setDebounced('');
                  setStatuses([]);
                  setBalanceDue(false);
                  setOverdue(false);
                  setFrom('');
                  setTo('');
                }}
              >
                {t('common:actions.clearFilters')}
              </Button>
            ) : null}
          </Toolbar>

          <CardBody density="flush">
            {query.isPending ? (
              <TableSkeleton rows={7} columns={8} label={t('common:states.loading')} />
            ) : query.isError ? (
              <ErrorState
                title={t('common:states.errorTitle')}
                description={errorMessage(query.error, t)}
                traceId={errorTraceId(query.error)}
                retryLabel={t('common:actions.retry')}
                onRetry={() => void query.refetch()}
                traceCopyLabel={t('common:actions.copy')}
                traceCopiedLabel={t('common:actions.copied')}
              />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<Receipt className="ico ico-24" aria-hidden="true" />}
                title={
                  hasFilters
                    ? t('billing:invoices.emptySearchTitle')
                    : t('billing:invoices.emptyTitle')
                }
                description={
                  hasFilters
                    ? t('billing:invoices.emptySearchBody')
                    : t('billing:invoices.emptyBody')
                }
              />
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('billing:invoices.columns.number')}</Th>
                      <Th>{t('billing:invoices.columns.billingParty')}</Th>
                      <Th>{t('billing:invoices.columns.reservation')}</Th>
                      <Th>{t('billing:invoices.columns.issueDate')}</Th>
                      <Th>{t('billing:invoices.columns.dueDate')}</Th>
                      <Th align="right">{t('billing:invoices.columns.net')}</Th>
                      <Th align="right">{t('billing:invoices.columns.balance')}</Th>
                      <Th>{t('billing:invoices.columns.status')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const late = overdueDays(row);
                      return (
                        <Tr key={row.id}>
                          <Td>
                            <Link to="/billing/invoices/$invoiceId" params={{ invoiceId: row.id }}>
                              <span className="cell-id">
                                {row.number ?? t('billing:status.invoice.DRAFT')}
                              </span>
                            </Link>
                          </Td>
                          <Td>
                            <UserCell
                              avatar={<Avatar name={row.billingParty.displayName} size="sm" />}
                              name={row.billingParty.displayName}
                              subtitle={row.guest?.displayName ?? undefined}
                            />
                          </Td>
                          <Td className="t-13 truncate" style={{ maxWidth: 260 }}>
                            {row.reservationReference ?? '—'}
                          </Td>
                          <Td className="t-13" numeric>
                            {row.issueDate ? formatDateShort(row.issueDate, i18n.language) : '—'}
                          </Td>
                          <Td className="t-13" numeric>
                            {row.dueDate ? formatDateShort(row.dueDate, i18n.language) : '—'}
                          </Td>
                          <Td align="right" numeric>
                            {formatAmount(row.totals.netTotal, i18n.language)}
                          </Td>
                          <Td align="right" numeric>
                            {row.status === 'DRAFT' ? (
                              <span className="muted">—</span>
                            ) : row.totals.balance > 0 ? (
                              <span
                                className="strong"
                                style={{ color: late > 0 ? 'var(--danger)' : 'var(--warning)' }}
                              >
                                {formatAmount(row.totals.balance, i18n.language)}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--success)' }}>
                                {formatAmount(0, i18n.language)}
                              </span>
                            )}
                          </Td>
                          <Td>
                            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                              {row.complianceOverridden ? (
                                <Badge tone="amber">
                                  <KeyRound className="ico ico-14" aria-hidden="true" />
                                  {t('billing:status.overridden')}
                                </Badge>
                              ) : (
                                <Badge tone={invoiceTone(row.status)} dot>
                                  {t(`billing:status.invoice.${row.status}`)}
                                </Badge>
                              )}
                              {late > 0 ? (
                                <Badge tone="red">
                                  <AlertTriangle className="ico ico-14" aria-hidden="true" />
                                  {t('billing:status.overdue', { count: late })}
                                </Badge>
                              ) : null}
                              {row.status === 'DRAFT' ? (
                                <Badge tone="red">
                                  <Lock className="ico ico-14" aria-hidden="true" />
                                  {t('billing:status.blocked')}
                                </Badge>
                              ) : null}
                            </div>
                          </Td>
                        </Tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </CardBody>

          {query.hasNextPage ? (
            <div className="card__foot">
              <Button
                variant="secondary"
                size="sm"
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {t('common:pagination.loadMore')}
              </Button>
            </div>
          ) : null}
        </Card>
      </div>
    </Container>
  );
}
